import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup
} from "firebase/auth";
import { useAuth } from "../lib/auth";
import { PasswordInput } from "../components/PasswordInput";

/**
 * Convierte una sesión anónima en cuenta permanente "linkeada" a Google o email/pass.
 * Todos los tickets, claims y data del user se preservan: es el MISMO uid, solo
 * gana un provider permanente.
 */
export function Upgrade() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState<"choose" | "email">("choose");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) {
    nav("/login", { replace: true });
    return null;
  }
  if (!user.isAnonymous) {
    nav("/perfil", { replace: true });
    return null;
  }

  const onGoogle = async () => {
    setBusy(true);
    setError(null);
    try {
      await linkWithPopup(user, new GoogleAuthProvider());
      nav("/perfil", { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const onEmail = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const cred = EmailAuthProvider.credential(email, password);
      await linkWithCredential(user, cred);
      nav("/perfil", { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col gap-6">
        <button onClick={() => nav(-1)} className="btn-ghost self-start px-3 py-2 text-sm">
          ← Volver
        </button>

        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza</p>
          <h1 className="mt-2 font-display text-2xl font-semibold text-leaf-950">
            Guarda tu progreso
          </h1>
          <p className="mt-2 text-sm text-leaf-700">
            Vincula tu cuenta para no perder tus tickets si cambias de celular o limpias el browser.
          </p>
        </header>

        <div className="card flex flex-col gap-4">
          {mode === "choose" && (
            <>
              <button onClick={onGoogle} disabled={busy} className="btn-secondary">
                <GoogleIcon />
                <span>Vincular con Google</span>
              </button>
              <button onClick={() => setMode("email")} disabled={busy} className="btn-ghost">
                Vincular con email y contraseña
              </button>
            </>
          )}

          {mode === "email" && (
            <form onSubmit={onEmail} className="flex flex-col gap-3">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
              />
              <PasswordInput
                autoComplete="new-password"
                required
                minLength={6}
                placeholder="Crear contraseña (6+ caracteres)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="submit" disabled={busy} className="btn-primary">
                {busy ? "Vinculando…" : "Vincular cuenta"}
              </button>
              <button
                type="button"
                onClick={() => setMode("choose")}
                className="btn-ghost text-sm"
              >
                ← Volver a opciones
              </button>
            </form>
          )}

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 18 18" className="h-5 w-5" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.92v2.32A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.92A9 9 0 0 0 0 9c0 1.45.35 2.82.92 4.04l3.05-2.32z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .92 4.96l3.05 2.32C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function messageFor(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/email-already-in-use":
    case "auth/credential-already-in-use":
      return "Ese email ya tiene cuenta. Cierra sesión y entra con esa cuenta.";
    case "auth/weak-password":
      return "La contraseña debe tener al menos 6 caracteres.";
    case "auth/popup-closed-by-user":
      return "Cancelaste el inicio de sesión con Google.";
    case "auth/network-request-failed":
      return "Sin conexión. Revisa tu internet.";
    default:
      return "No pudimos vincular tu cuenta. Inténtalo de nuevo.";
  }
}
